"use client";
import { Api } from "@/Apis";
import {
  ApiResultOfLandingAppointmentCanBookingOutputApiModel,
  SelectLandingCenterBranchApiModel,
  SelectLandingCenterDoctorApiModel,
  SelectLandingCenterItemApiModel,
  SelectLandingWorkDayApiModel,
  SelectLandingWorkTimeApiModel,
} from "@/Apis/ClinicApi";
import { useCommonAccountModal } from "@/Areas/Common/Pages/CommonAccount";
import {
  CommonField,
  CommonSelect,
  Format,
  Html,
  If,
  SmartLink,
} from "@/Components";
import { CommonFieldHandle } from "@/Components/CommonField";
import { Formats, Helper, Modules } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { Constants, formProps } from "@/Helpers/Constants";
import {
  useAccount,
  useApp,
  useCenterOptionEnums,
  useResponsive,
  useT,
  useTransactionNavigation
} from "@/Hooks";
import {
  CalendarOutlined,
  CaretLeftOutlined,
  CaretRightOutlined,
  CheckCircleTwoTone,
  ClockCircleOutlined,
  NumberOutlined,
  PhoneOutlined,
  StarFilled,
  UserOutlined,
} from "@ant-design/icons";
import {
  CheckCard,
  useDeepCompareEffectDebounce,
} from "@ant-design/pro-components";
import {
  Alert,
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Row,
  Space,
  Spin,
  theme,
  Typography,
} from "antd";
import { DefaultOptionType } from "antd/es/select";
import dayjs from "dayjs";
import _ from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import useLandingAppointmentModal from "../useLandingAppointmentModal";

export interface LandingAppointmentBookingPageForm {
  CenterId?: number;
  CenterBranchId?: number;
  CenterUserDoctorId: number;
  ServiceId: number;
  Date: string;
  WorkTimeId: number;
  ForMyself: boolean;
}

export interface LandingAppointmentBookingPageProps {
  values?: Partial<LandingAppointmentBookingPageForm>;
  callback?: (result: boolean) => void;
}

const LandingAppointmentBookingPage: FC<LandingAppointmentBookingPageProps> = ({
  values,
  callback,
}) => {
  const t = useT();
  const { message, modal, openShare } = useApp();
  const { isSignIn, profile } = useAccount();
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(false);
  const transactionNavigation = useTransactionNavigation();
  const [workTimes, setWorkTimes] = useState<SelectLandingWorkTimeApiModel[]>(
    [],
  );
  const [doctor, setDoctor] = useState<SelectLandingCenterDoctorApiModel>();
  const [service, setService] = useState<SelectLandingCenterItemApiModel>();
  const [centerBranch, setCenterBranch] =
    useState<SelectLandingCenterBranchApiModel>();
  const [canBooking, setCanBooking] =
    useState<ApiResultOfLandingAppointmentCanBookingOutputApiModel>({
      Success: true,
    });
  const [form] = Form.useForm<LandingAppointmentBookingPageForm>();
  const landingAppointmentModal = useLandingAppointmentModal();
  const { getLabel: getShiftWorkLabel } = useCenterOptionEnums({ parentKey: CenterOptions.Enum.ShiftWork.key });
  const commonAccountModal = useCommonAccountModal();
  const formCenterId = Form.useWatch("CenterId", form);
  const formCenterBranchId = Form.useWatch("CenterBranchId", form);
  const formCenterUserDoctorId = Form.useWatch("CenterUserDoctorId", form);
  const formServiceId = Form.useWatch("ServiceId", form);
  const formDate = Form.useWatch("Date", form);
  const formWorkTimeId = Form.useWatch("WorkTimeId", form);
  const centerIdRef = useRef<number | undefined>(values?.CenterId);
  const centerBranchIdRef = useRef<number | undefined>(values?.CenterBranchId);
  const centerUserDoctorIdRef = useRef<number | undefined>(
    values?.CenterUserDoctorId,
  );
  const serviceIdRef = useRef<number | undefined>(values?.ServiceId);
  const skipInitialCenterCascadeResetRef = useRef(
    !!(
      values?.CenterBranchId ||
      values?.CenterUserDoctorId ||
      values?.ServiceId ||
      values?.Date
    ),
  );
  const skipInitialCenterBranchCascadeResetRef = useRef(
    !!(values?.CenterUserDoctorId || values?.ServiceId || values?.Date),
  );
  const skipInitialDoctorCascadeResetRef = useRef(
    !!(values?.ServiceId || values?.Date),
  );
  const skipInitialServiceCascadeResetRef = useRef(!!values?.Date);

  useEffect(() => {
    centerIdRef.current = formCenterId;
  }, [formCenterId]);

  useEffect(() => {
    centerBranchIdRef.current = formCenterBranchId;
  }, [formCenterBranchId]);

  useEffect(() => {
    centerUserDoctorIdRef.current = formCenterUserDoctorId;
  }, [formCenterUserDoctorId]);

  useEffect(() => {
    serviceIdRef.current = formServiceId;
  }, [formServiceId]);

  useDeepCompareEffectDebounce(
    () => {
      booking();
    },
    [formWorkTimeId],
    [],
    500,
  );

  useEffect(() => {
    const load = async () => {
      if (
        formCenterBranchId &&
        formCenterUserDoctorId &&
        formServiceId &&
        service
      ) {
        setLoading(true);
        const r = await Api.LandingAppointment.canBooking({
          CenterBranchId: formCenterBranchId,
          CenterUserDoctorId: formCenterUserDoctorId,
          ServiceId: formServiceId,
        });
        setCanBooking(r);
        // if (!Success) {
        //     message.errors(Errors);
        //     // callback?.(false);
        // }
        setLoading(false);
      }
    };
    load();
  }, [
    formCenterBranchId,
    formCenterUserDoctorId,
    formServiceId,
    isSignIn,
    service,
  ]);
  const booking = async () => {
    if (!formWorkTimeId) return;
    if (!isSignIn) {
      message.error(
        t("landing.contact.message.signInRequired", undefined, "Booking"),
      );
      commonAccountModal.showSignIn({
        redirectToUserArea: false,
        completeSignUpValues: {
          CenterId: formCenterId,
          CenterUserDoctorId: formCenterUserDoctorId,
        },
      });
      form.setFieldValue("WorkTimeId", undefined);
      return;
    }
    try {
      const values = await form.validateFields();
      const workTime = workTimes.find((w) => w.Id == values.WorkTimeId);
      if (!workTime) {
        form.setFieldValue("WorkTimeId", undefined);
        return;
      }
      if (workTime.IsSpecial && !profile?.IsSpecial) {
        message.error(
          t("landing.appointment.booking.message", undefined, "Booking"),
        );
        form.setFieldValue("WorkTimeId", undefined);
        return;
      }
      const { Confirmed, ForMyself } =
        await landingAppointmentModal.showReservationConfirmation({
          WorkTimeAlias: workTime.Alias,
          ServiceOnlinePaymentStatus: service?.OnlinePaymentStatusKey,
        });

      if (!Confirmed) {
        form.setFieldValue("WorkTimeId", undefined);
        return;
      }
      setLoading(true);
      let r = await Api.LandingAppointment.booking({ ...values, ForMyself });
      if (r.Result) {
        if (!ForMyself) {
          const r2 = await landingAppointmentModal.showSetCenterUser({
            CenterId: formCenterId!,
            CenterInvoiceId: r.Result.CenterInvoiceId,
            ExpirationDate: Helper.dayjsMiladi(r.Result.ExpirationDate)!,
          });
          if (!r2) {
            workTimeIdFieldRef.current?.refresh();
            form.setFieldValue("WorkTimeId", undefined);
            return;
          }
          r = await Api.LandingAppointment.getBookingResult({
            CenterInvoiceId: r.Result.CenterInvoiceId,
          });
          if (!r.Result) {
            workTimeIdFieldRef.current?.refresh();
            form.setFieldValue("WorkTimeId", undefined);
            return;
          }
        }
        if (r.Result.TransactionId) {
          transactionNavigation.selectPaymentMethod(r.Result.TransactionId);
          callback?.(true);
        } else {
          form.setFieldValue("WorkTimeId", undefined);
          workTimeIdFieldRef.current?.refresh();
          modal.success({
            width: 650,
            icon: null,
            title: (
              <Flex vertical align="center" style={{ gap: 8 }}>
                <CheckCircleTwoTone
                  twoToneColor="#52c41a"
                  style={{ fontSize: 64 }}
                />
                <h2 style={{ margin: 0 }}>
                  {t(
                    "landing.appointment.booking.reservationSuccessful",
                    undefined,
                    "Booking",
                  )}
                </h2>
              </Flex>
            ),
            centered: true,
            closable: true,
            onCancel: () => form.resetFields(),
            content: (
              <div style={{ padding: "16px 8px", textAlign: "center" }}>
                <Flex
                  vertical
                  align="start"
                  style={{ maxWidth: 400, margin: "0 auto", gap: 8 }}
                >
                  <p>
                    <UserOutlined />{" "}
                    {r.Result.DoctorNicknameSingular ??
                      t(
                        "common.field.serviceProvider",
                        undefined,
                        "Booking",
                      )}{" "}
                    :{" "}
                    <SmartLink href={r.Result.CenterUserDoctorProfileLink}>
                      <b>{r.Result.CenterUserDoctorFullName}</b>
                    </SmartLink>
                  </p>
                  <p>
                    <CalendarOutlined />{" "}
                    {t(
                      "landing.appointment.booking.dateLabel",
                      undefined,
                      "Booking",
                    )}{" "}
                    <Format type="Date" value={r.Result.Date} />
                  </p>
                  <p>
                    <ClockCircleOutlined />{" "}
                    {t(
                      "landing.appointment.booking.timeLabel",
                      undefined,
                      "Booking",
                    )}{" "}
                    <Format type="Time" value={r.Result.Date} />
                  </p>
                  <p>
                    <NumberOutlined />{" "}
                    {t(
                      "landing.appointment.booking.trackingNumberLabel",
                      undefined,
                      "Booking",
                    )}{" "}
                    <b>{r.Result.TrackingCode}</b>
                  </p>
                  <p>
                    <UserOutlined />{" "}
                    {t(
                      "landing.appointment.booking.clientLabel",
                      undefined,
                      "Booking",
                    )}{" "}
                    {r.Result.CenterUserFullName}
                  </p>
                  <p>
                    <PhoneOutlined />{" "}
                    {t(
                      "landing.appointment.booking.mobileLabel",
                      undefined,
                      "Booking",
                    )}
                    {r.Result.CenterUserPhoneNumber}
                  </p>
                </Flex>

                <Space style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    href="/Customer/Appointment/List"
                    size="large"
                  >
                    {t(
                      "landing.appointment.booking.viewMyAppointments",
                      undefined,
                      "Booking",
                    )}
                  </Button>
                  {r.Result && (
                    <Button
                      type="primary"
                      onClick={() => {
                        if (r.Result)
                          openShare({
                            link: Constants.SiteAddress,
                            text: t(
                              "landing.appointment.booking.appointmentReservationSuccessMessage",
                              {
                                value0: r.Result.CenterUserDoctorFullName,
                                value1: r.Result.CenterUserFullName,
                                value2: Formats.Date(r.Result.Date),
                                value3: Formats.Time(r.Result.Date),
                                value4: r.Result.TrackingCode,
                                value5: r.Result.CenterBranchName,
                                value6: Constants.SiteAddress,
                              },
                              "Appointment reservation share text",
                            ),
                          });
                      }}
                      size="large"
                    >
                      {t("common.action.share", undefined, "Booking")}
                    </Button>
                  )}
                </Space>

                <Divider style={{ margin: "24px 0" }} />

                <div style={{ textAlign: "right", direction: "rtl" }}>
                  <h4>
                    {t(
                      "center.branch.field.appointmentBookingRules",
                      undefined,
                      "Booking",
                    )}
                  </h4>
                  <Html value={r.Result.AppointmentBookingRules} />

                  <h4 style={{ marginTop: 16 }}>
                    {t(
                      "center.branch.field.appointmentCancellationRules",
                      undefined,
                      "Booking",
                    )}
                  </h4>
                  <Html value={r.Result.AppointmentCancellationRules} />
                </div>
              </div>
            ),
            okButtonProps: { style: { display: "none" } },
          });
          callback?.(true);
        }
      } else {
        form.setFieldValue("WorkTimeId", undefined);
        message.errors(r.Errors);
        if (r.Errors?.[0]?.Key == "TimeSlotAlreadyBooked") {
          workTimeIdFieldRef.current?.refresh();
        }
      }
    } catch (error) { }
    setLoading(false);
  };
  const scrollRef = useRef<HTMLDivElement>(null);
  const workTimeIdFieldRef = useRef<CommonFieldHandle>(null);

  const { token } = theme.useToken();
  return (
    <Spin spinning={loading}>
      <Row justify="center">
        <Col md={24} xs={24}>
          <Form
            {...formProps}
            initialValues={{
              ...values,
              // UniqueCode: new Date().getTime(),
              // Id,
              // MonitoringAnswer: { TableKey: CenterOptions.Table.CenterOpportunity.key },
            }}
            // onFinish={onFinish}
            form={form}
          >
            {/* <Form.Item hidden name='CenterId'><Input /></Form.Item> */}

            <Row>
              <If condition={!values?.CenterId} exists>
                <Col md={12} xs={24}>
                  <Form.Item
                    name="CenterId"
                    label={t("common.field.center", undefined, "Booking")}
                  >
                    <CommonSelect
                      sourceType="Landing"
                      methodName="Centers"
                      model={{
                        HasModule: Modules.InternetScheduling,
                      }}
                      onChange={(value) => {
                        if (
                          skipInitialCenterCascadeResetRef.current &&
                          centerIdRef.current === undefined
                        ) {
                          skipInitialCenterCascadeResetRef.current = false;
                          return;
                        }
                        skipInitialCenterCascadeResetRef.current = false;
                        if (value === centerIdRef.current) return;
                        form.setFieldValue("CenterBranchId", undefined);
                        form.setFieldValue("CenterUserDoctorId", undefined);
                        form.setFieldValue("ServiceId", undefined);
                        form.setFieldValue("Date", undefined);
                        setService(undefined);
                      }}
                    />
                  </Form.Item>
                </Col>
              </If>
              <If condition={!values?.CenterBranchId} exists>
                <Col md={12} xs={24}>
                  <Form.Item
                    name="CenterBranchId"
                    label={t("common.field.branch", undefined, "Booking")}
                    rules={[{ required: true }]}
                  >
                    <CommonSelect
                      sourceType="Landing"
                      methodName="CenterBranches"
                      onValueChange={(option: DefaultOptionType | undefined) =>
                        setCenterBranch(option?.Data)
                      }
                      autoSelectFirst={!!formCenterId}
                      model={{
                        CenterId: formCenterId,
                        HasModule: Modules.InternetScheduling,
                      }}
                      onChange={(value) => {
                        if (
                          skipInitialCenterBranchCascadeResetRef.current &&
                          centerBranchIdRef.current === undefined
                        ) {
                          skipInitialCenterBranchCascadeResetRef.current = false;
                          return;
                        }
                        skipInitialCenterBranchCascadeResetRef.current = false;
                        if (value === centerBranchIdRef.current) return;
                        form.setFieldValue("CenterUserDoctorId", undefined);
                        form.setFieldValue("ServiceId", undefined);
                        form.setFieldValue("Date", undefined);
                        setService(undefined);
                      }}
                    />
                  </Form.Item>
                </Col>
              </If>
              <Col md={12} xs={24}>
                <Form.Item
                  name="CenterUserDoctorId"
                  label={
                    centerBranch?.DoctorNicknameSingular ??
                    t("common.field.serviceProvider", undefined, "Booking")
                  }
                  rules={[{ required: true }]}
                  extra={
                    <Space orientation="vertical">
                      {doctor?.Link && (
                        <SmartLink href={doctor.Link}>
                          <Typography.Text>
                            {t(
                              "landing.appointment.booking.openServiceProviderProfile",
                              undefined,
                              "Booking",
                            )}
                          </Typography.Text>
                        </SmartLink>
                      )}
                      {doctor?.CenterUserBranchId && (
                        <Typography.Text
                          copyable={{
                            text: `${window.location.origin}/Nobat/${doctor.CenterUserBranchId}`,
                          }}
                        >
                          {t(
                            "landing.appointment.booking.copyAppointmentLink",
                            undefined,
                            "Booking",
                          )}
                        </Typography.Text>
                      )}
                    </Space>
                  }
                >
                  <CommonSelect
                    sourceType="Landing"
                    methodName="Doctors"
                    onValueChange={(option: DefaultOptionType | undefined) =>
                      setDoctor(option?.Data)
                    }
                    // autoSelectFirst={!!formCenterBranchId}
                    autoSelectWhenThereIsOne={!!formCenterBranchId}
                    model={{
                      CenterId: formCenterId,
                      CenterBranchId: formCenterBranchId,
                      HasWorkDaysAndTimesWithInternetAppointment: true,
                      HasModule: Modules.InternetScheduling,
                    }}
                    onChange={(value) => {
                      if (
                        skipInitialDoctorCascadeResetRef.current &&
                        centerUserDoctorIdRef.current === undefined
                      ) {
                        skipInitialDoctorCascadeResetRef.current = false;
                        return;
                      }
                      skipInitialDoctorCascadeResetRef.current = false;
                      if (value === centerUserDoctorIdRef.current) return;
                      form.setFieldValue("ServiceId", undefined);
                      form.setFieldValue("Date", undefined);
                      setService(undefined);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col md={12} xs={24}>
                <Form.Item
                  name="ServiceId"
                  label={t("common.field.service", undefined, "Booking")}
                  rules={[{ required: true }]}
                  extra={
                    service && (
                      <Space orientation="vertical">
                        <Space>
                          {t(
                            "landing.appointment.booking.serviceAmountLabel",
                            undefined,
                            "Booking",
                          )}{" "}
                          <Format
                            type="Number"
                            value={service?.SalePrice}
                            currency={centerBranch?.Currency}
                          />{" "}
                          |{" "}
                          {service?.OnlinePaymentStatusKey ==
                            CenterOptions.Enum.Status.Active.key
                            ? t(
                              "center.departmentService.field.onlinePaymentStatus",
                              undefined,
                              "Booking",
                            )
                            : t(
                              "landing.appointment.booking.payInPerson",
                              undefined,
                              "Booking",
                            )}
                        </Space>
                        <SmartLink href={service.Link}>
                          {t(
                            "landing.appointment.booking.viewServiceDetails",
                            undefined,
                            "Booking",
                          )}
                        </SmartLink>
                      </Space>
                    )
                  }
                >
                  <CommonSelect
                    sourceType="Landing"
                    methodName="CenterItems"
                    onValueChange={(option: DefaultOptionType | undefined) =>
                      setService(option?.Data)
                    }
                    autoSelectFirst={!!formCenterUserDoctorId}
                    model={{
                      CenterId: formCenterId,
                      CenterBranchId: formCenterBranchId,
                      CenterUserDoctorId: formCenterUserDoctorId,
                      InternetAppointmentStatusKey:
                        CenterOptions.Enum.Status.Active.key,
                      DepartmentTypeKey:
                        CenterOptions.Enum.CenterDepartmentType.Service.key,
                    }}
                    onChange={(value) => {
                      if (
                        skipInitialServiceCascadeResetRef.current &&
                        serviceIdRef.current === undefined
                      ) {
                        skipInitialServiceCascadeResetRef.current = false;
                        return;
                      }
                      skipInitialServiceCascadeResetRef.current = false;
                      if (value === serviceIdRef.current) return;
                      form.setFieldValue("Date", undefined);
                    }}
                  />
                </Form.Item>
              </Col>
              {canBooking.Success ? (
                <>
                  {formCenterUserDoctorId && formServiceId && (
                    <Col xs={24}>
                      <Form.Item
                        name="Date"
                        label={t("common.unit.day", undefined, "Booking")}
                        rules={[{ required: true }]}
                        noStyle
                      >
                        <CommonField
                          sourceType="Landing"
                          methodName="WorkDays"
                          model={{
                            CenterBranchId: formCenterBranchId,
                            CenterUserDoctorId: formCenterUserDoctorId,
                            ServiceId: formServiceId,
                          }}
                          onOptionsChange={(options) => {
                            if (options.length > 0) {
                              // && !form.getFieldValue("Date")
                              form.setFieldValue("Date", options[0].value);
                            }
                          }}
                          render={({ options, loading, ...rest }) => {
                            const items = options
                              .map(
                                (option) =>
                                  option.Data as SelectLandingWorkDayApiModel,
                              )
                              .filter((item) => item.WorkTimesCount != 0);

                            if (!loading && _.isEmpty(items))
                              return (
                                <Alert
                                  description={t(
                                    "landing.appointment.booking.appointmentName",
                                    undefined,
                                    "Booking",
                                  )}
                                  type="error"
                                  showIcon
                                />
                              );
                            const scroll = (direction: "left" | "right") => {
                              if (scrollRef.current) {
                                const { clientWidth } = scrollRef.current;
                                scrollRef.current.scrollBy({
                                  left:
                                    direction === "left"
                                      ? clientWidth
                                      : -clientWidth,
                                  behavior: "smooth",
                                });
                              }
                            };

                            return (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <If condition={!isMobile}>
                                  <Button
                                    onClick={() => scroll("left")}
                                    shape="circle"
                                    icon={<CaretRightOutlined />}
                                    style={{
                                      marginInline: 4,
                                      width: 32,
                                      height: 32,
                                    }}
                                  />
                                </If>

                                <CheckCard.Group
                                  {...rest}
                                  loading={loading}
                                  size="small"
                                  style={{
                                    width: isMobile
                                      ? "100%"
                                      : "calc(100% - 80px)",
                                    // height: 70,
                                  }}
                                >
                                  <div
                                    ref={scrollRef}
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      overflowX: "auto",
                                      padding: 8,
                                    }}
                                  >
                                    {items.map((item) => {
                                      const date = dayjs(item.Date);
                                      return (
                                        <CheckCard
                                          key={item.Date}
                                          value={item.Date}
                                          size="small"
                                          style={{
                                            margin: 0,
                                            width: 100,
                                            flexShrink: 0,
                                            textAlign: "center",
                                          }}
                                          title={
                                            <div
                                              style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                              }}
                                            >
                                              <span>{date.format("dddd")}</span>
                                              <span>
                                                {date.format("D MMMM")}
                                              </span>
                                            </div>
                                          }
                                          description={
                                            item.IsHoliday
                                              ? t(
                                                "landing.appointment.booking.closed",
                                                undefined,
                                                "Booking",
                                              )
                                              : item.FreeWorkTimesCount === 0
                                                ? "---"
                                                : t(
                                                  "landing.appointment.booking.appointment",
                                                  {
                                                    value0:
                                                      item.FreeWorkTimesCount,
                                                  },
                                                  "Booking",
                                                )
                                          }
                                          loading={loading}
                                          disabled={item.IsHoliday}
                                        />
                                      );
                                    })}
                                  </div>
                                </CheckCard.Group>

                                <If condition={!isMobile}>
                                  <Button
                                    onClick={() => scroll("right")}
                                    shape="circle"
                                    icon={<CaretLeftOutlined />}
                                    style={{
                                      marginInline: 4,
                                      width: 32,
                                      height: 32,
                                    }}
                                  />
                                </If>
                              </div>
                            );
                          }}
                        />
                        {/* <CommonCheckCard dataSource="Landing.WorkDays" model={{
                                    CenterBranchId: formCenterBranchId,
                                    CenterUserDoctorId: formCenterUserDoctorId,
                                    ServiceId: formServiceId,
                                }} /> */}
                      </Form.Item>
                    </Col>
                  )}

                  {formCenterUserDoctorId && formServiceId && formDate && (
                    <Col md={24} xs={24} style={{ marginTop: 10 }}>
                      <Flex vertical>
                        <Form.Item
                          name="WorkTimeId"
                          label={t("common.field.time", undefined, "Booking")}
                          rules={[{ required: true }]}
                          noStyle
                        >
                          <CommonField
                            ref={workTimeIdFieldRef}
                            // onSelect={booking}
                            sourceType="Landing"
                            methodName="WorkTimes"
                            model={{
                              CenterBranchId: formCenterBranchId,
                              Date: formDate,
                              CenterUserDoctorId: formCenterUserDoctorId,
                              ServiceId: formServiceId,
                            }}
                            onOptionsChange={(options) => {
                              const items = options.map(
                                (opt) =>
                                  opt.Data as SelectLandingWorkTimeApiModel,
                              );
                              setWorkTimes(items);
                            }}
                            render={({ options, loading, errors, ...rest }) => {
                              const items = options.map(
                                (option) =>
                                  option.Data as SelectLandingWorkTimeApiModel,
                              );
                              const shifts = _.uniq(
                                items.map((item) => item.ShiftKey),
                              );
                              const error = errors?.[0]?.Value;

                              if (!loading && _.isEmpty(items))
                                return (
                                  <Alert
                                    description={
                                      error ??
                                      t(
                                        "landing.appointment.booking.appointmentService",
                                        undefined,
                                        "Booking",
                                      )
                                    }
                                    type="error"
                                    showIcon
                                  />
                                );

                              return (
                                <CheckCard.Group
                                  {...rest}
                                  loading={loading}
                                  size="small"
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      flexWrap: "wrap",
                                      padding: 8,
                                      width: "100%",
                                    }}
                                  >
                                    {shifts.map((shift) => (
                                      <>
                                        <Divider key={shift}>
                                          {t(
                                            "common.field.shift",
                                            undefined,
                                            "Booking",
                                          )}{" "}
                                          <q>
                                            {getShiftWorkLabel(shift)}
                                          </q>
                                        </Divider>
                                        {items
                                          .filter(
                                            (item) => item.ShiftKey == shift,
                                          )
                                          .map((item) => {
                                            const getTitle = () => {
                                              if (item.IsExpired)
                                                return t(
                                                  "common.status.expired",
                                                  undefined,
                                                  "Booking",
                                                );
                                              if (item.IsAwaitingPayment)
                                                return t(
                                                  "landing.appointment.booking.status.bookingInProgress",
                                                  undefined,
                                                  "Booking",
                                                );
                                              if (item.IsReserved)
                                                return t(
                                                  "landing.appointment.booking.reserved",
                                                  undefined,
                                                  "Booking",
                                                );
                                              if (item.IsSpecial)
                                                return t(
                                                  "landing.appointment.booking.special",
                                                  undefined,
                                                  "Booking",
                                                );
                                              return t(
                                                "landing.appointment.booking.status.bookable",
                                                undefined,
                                                "Booking",
                                              );
                                            };
                                            const getBackgroundColor = () => {
                                              if (item.IsExpired)
                                                return token.colorFillTertiary;
                                              if (item.IsAwaitingPayment)
                                                return token.colorWarningBg;
                                              if (item.IsReserved)
                                                return token.colorErrorBg;
                                              if (item.IsSpecial)
                                                return token.colorInfoBg;
                                              return token.colorSuccessBg;
                                            };
                                            return (
                                              <CheckCard
                                                key={item.Id}
                                                value={item.Id}
                                                size="small"
                                                style={{
                                                  margin: 0,
                                                  width: isMobile
                                                    ? "30%"
                                                    : "auto",
                                                  flexShrink: 0,
                                                  textAlign: "center",
                                                  backgroundColor:
                                                    getBackgroundColor(),
                                                  color: token.colorText,
                                                  borderColor: item.IsReserved
                                                    ? token.colorErrorBorder
                                                    : token.colorBorder,
                                                }}
                                                title={
                                                  <span>
                                                    {item.Alias}
                                                    {item.IsSpecial && (
                                                      <StarFilled
                                                        style={{
                                                          color: "#faad14",
                                                          marginLeft: 4,
                                                        }}
                                                      />
                                                    )}
                                                  </span>
                                                }
                                                description={getTitle()}
                                                disabled={!item.Status}
                                                loading={loading}
                                              />
                                            );
                                          })}
                                      </>
                                    ))}
                                  </div>
                                </CheckCard.Group>
                              );
                            }}
                          />
                        </Form.Item>
                        <Alert
                          message={
                            <Flex
                              gap={16}
                              wrap="wrap"
                              style={{ marginBottom: 8 }}
                            >
                              {t(
                                "landing.appointment.booking.guideLabel",
                                undefined,
                                "Booking",
                              )}
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: token.colorSuccessBg,
                                    border: `1px solid ${token.colorBorder}`,
                                  }}
                                />
                                {t(
                                  "landing.appointment.booking.status.bookable",
                                  undefined,
                                  "Booking",
                                )}
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: token.colorWarningBg,
                                    border: `1px solid ${token.colorBorder}`,
                                  }}
                                />
                                {t(
                                  "landing.appointment.booking.status.bookingInProgress",
                                  undefined,
                                  "Booking",
                                )}
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: token.colorErrorBg,
                                    border: `1px solid ${token.colorErrorBorder}`,
                                  }}
                                />
                                {t(
                                  "landing.appointment.booking.reserved",
                                  undefined,
                                  "Booking",
                                )}
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: token.colorFillTertiary,
                                    border: `1px solid ${token.colorBorder}`,
                                  }}
                                />
                                {t(
                                  "common.status.expired",
                                  undefined,
                                  "Booking",
                                )}
                              </span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <div
                                  style={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: token.colorInfoBg,
                                    border: `1px solid ${token.colorBorder}`,
                                  }}
                                />
                                {t(
                                  "landing.appointment.booking.specialUserRequired",
                                  undefined,
                                  "Booking",
                                )}{" "}
                                <SmartLink
                                  href={`/Item/${Modules.BronzCustomerSpecial}`}
                                  target="_blank"
                                >
                                  {t(
                                    "landing.appointment.booking.purchaseInParentheses",
                                    undefined,
                                    "Booking",
                                  )}
                                </SmartLink>
                                <StarFilled style={{ color: "#faad14" }} />
                              </span>
                            </Flex>
                          }
                          type="info"
                        />
                      </Flex>
                    </Col>
                  )}
                </>
              ) : (
                <Col md={24} xs={24} style={{ marginTop: 10 }}>
                  <Alert
                    description={
                      <Space orientation="vertical">
                        {canBooking.Errors?.map((error) => {
                          return (
                            <Flex key={error.Value} justify="space-between">
                              {error.Value}
                              <If
                                condition={
                                  error.Key == "NeedSignIn" && !isSignIn
                                }
                              >
                                <Button
                                  onClick={() =>
                                    commonAccountModal.showSignIn({
                                      redirectToUserArea: false,
                                      completeSignUpValues: {
                                        CenterId: formCenterId,
                                        CenterUserDoctorId:
                                          formCenterUserDoctorId,
                                      },
                                    })
                                  }
                                >
                                  {t(
                                    "landing.appointment.booking.loginRegister",
                                    undefined,
                                    "Booking",
                                  )}
                                </Button>
                              </If>
                              <If condition={isSignIn}>
                                <SmartLink href="/Customer/Appointment/List">
                                  <Button>
                                    {t(
                                      "landing.appointment.booking.myAppointments",
                                      undefined,
                                      "Booking",
                                    )}
                                  </Button>
                                </SmartLink>
                              </If>
                            </Flex>
                          );
                        })}
                      </Space>
                    }
                    type="error"
                    showIcon
                  />
                </Col>
              )}

              {/* <Button onClick={() => {
                        workTimeIdFieldRef.current?.refresh();
                    }}>refresh</Button> */}

              {/* <If condition={Helper.isDev()}>
                        <Form.Item shouldUpdate={() => true} noStyle>
                            {() => <JSONPretty data={form.getFieldsValue()} />}
                        </Form.Item>
                    </If> */}
            </Row>
          </Form>
        </Col>
      </Row>
    </Spin>
  );
};

export default LandingAppointmentBookingPage;
